import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useRecipe} from '@chakra-ui/react3'
import navigationHideRecipe from './index.recipe'

/**
 * Applies the CMS `hide` flag, which every level of the navigation tree carries
 * on its entries, teasers and separators.
 *
 * The flag is honoured as responsive CSS, never by branching on a breakpoint in
 * JavaScript — see `index.recipe.js` for why. The wrapper is `display:
 * contents` by default, so it adds no box of its own and the wrapped element
 * keeps its layout role in the parent. An element that needs a real box — one
 * being animated, say — asks for it by setting `--navigation-hide-display`.
 *
 * Pass `asChild` to merge onto the wrapped element instead of rendering a
 * wrapper — the usual case for a list item, whose `li` semantics must stay a
 * direct child of the list.
 *
 * @component
 * @param {Object} props
 * @param {'none'|'mobile'|'desktop'} [props.hide='none'] - Viewport to hide the
 *   entry on. `'none'` is a CMS sentinel meaning "show everywhere", not absence.
 * @param {boolean} [props.asChild] - Merge the styles onto the single child
 *   element rather than rendering a wrapper.
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 *
 * @example
 * <NavigationHide asChild hide={item.hide}>
 *     <Box as="li">…</Box>
 * </NavigationHide>
 */
const NavigationHide = forwardRef(({hide, ...rest}, ref) => {
    const recipe = useRecipe({recipe: navigationHideRecipe})
    const styles = useMemo(() => recipe({hide}), [recipe, hide])

    return <Box ref={ref} css={styles} {...rest} />
})

NavigationHide.propTypes = {
    hide: PropTypes.oneOf(Object.keys(navigationHideRecipe.variants.hide)),
    asChild: PropTypes.bool,
    children: PropTypes.node
}

if (process.env.NODE_ENV !== 'production') {
    NavigationHide.displayName = 'NavigationHide'
}

export default NavigationHide
